/**
 * FCr Query Layer — brain step 3 (FVC 171)
 *
 * Sits ON TOP of the StorageAdapter interface. Storage-agnostic — the same
 * queries work identically on LocalStorageAdapter, IndexedDBAdapter, and
 * PocketBaseAdapter.
 *
 * Method set (minimum per FVC 171 dispatch):
 *   - list(kind, opts?)         — fetch all entities of a kind (with filters)
 *   - resolve(id)               — load one entity, inlining its link targets
 *   - traverse(id, opts?)       — walk the graph from a root entity
 *
 * Adapters are not modified. The query layer uses only public adapter
 * methods (list, load) plus the EntityKind taxonomy from FVC 170.
 *
 * All filtering is in-memory after fetch. For very large stores, an
 * adapter-level index would be a future optimization (out of scope for
 * brain step 3).
 */

import type { StorageAdapter } from '../storage/types'
import type { EntityKind } from '../adapters/filesystem/types'

// ─── Public types ──────────────────────────────────────────────────────────

export interface ListOptions {
  /** Filter: entity.project === project */
  project?: string
  /** Filter: entity.createdAt > after (ISO 8601 string) */
  after?: string
  /** Filter: entity.createdAt < before (ISO 8601 string) */
  before?: string
  /** Max entities returned */
  limit?: number
  /** Skip first N entities (after filtering) */
  offset?: number
  /**
   * F 282 — opaque resume token, ADDITIVE beside `offset`.
   *
   * `offset` is a position, so an insert ahead of it shifts every later row and
   * the reader sees a duplicate. A cursor is a position in SORTED order
   * ((createdAt, id)), so a new row — which sorts after an existing one — does
   * not disturb the page boundary.
   *
   * `offset`, `after` and `before` are untouched and keep working. A caller
   * that wants a positional skip still gets one.
   */
  cursor?: string
}

/**
 * F 282 — one page of results, with the token to resume after it.
 *
 * `list` keeps returning `T[]` and is unchanged. This is the additive sibling
 * for callers that need a stable boundary: an HTTP route, a feed, anything
 * that will be read twice.
 */
export interface Page<T = unknown> {
  rows: T[]
  /** Null on the last page. Opaque to callers: do not parse it. */
  nextCursor: string | null
}

/**
 * F 282 — what a caller is, for the fleet-scope check.
 *
 * The policy is enforced HERE, in the query layer, and not in the HTTP route,
 * so that every caller inherits it. A route that forgot the check would still
 * be denied; a route cannot widen access by omission.
 */
export interface QueryCaller {
  /** The project doing the reading. */
  project: string
  /**
   * True if this project holds the declared 'fleet-read' capability. Resolved
   * by the caller from the project's record — the query layer does not read
   * ProjectStore, so it stays storage-agnostic and testable in isolation.
   */
  hasFleetRead?: boolean
}

/** F 282 — a stable (createdAt, id) boundary, encoded. Opaque by construction. */
type CursorPoint = { c: string; i: string }

function encodeCursor(point: CursorPoint): string {
  return Buffer.from(JSON.stringify(point), 'utf8').toString('base64url')
}

function decodeCursor(raw: string): CursorPoint {
  let parsed: unknown
  try {
    parsed = JSON.parse(Buffer.from(raw, 'base64url').toString('utf8'))
  } catch {
    throw new QueryLayerError('[QueryLayer.listPage] cursor is not a valid token')
  }
  const pt = parsed as Partial<CursorPoint>
  if (typeof pt?.c !== 'string' || typeof pt?.i !== 'string') {
    throw new QueryLayerError('[QueryLayer.listPage] cursor is not a valid token')
  }
  return { c: pt.c, i: pt.i }
}

/** A single resolved link target — entity inlined, or null if unreachable. */
export interface ResolvedLinkTarget<T = unknown> {
  id: string
  fcid: string
  entity: T | null
}

export interface ResolvedEntity<T = unknown> {
  entity: T
  fcid: string
  links: Array<{
    field: string
    target: ResolvedLinkTarget<T> | null
  }>
}

export interface TraverseOptions {
  /** Max graph depth. Default 3, hard max 8. */
  depth?: number
  /** Restrict traversal to these kinds. Default: all kinds. */
  kinds?: EntityKind[]
}

export interface TraverseNode {
  id: string
  fcid: string
  kind: EntityKind
  depth: number
}

export interface TraverseEdge {
  from: string
  to: string
  field: string
}

export interface TraversalResult {
  root: { id: string; fcid: string; kind: EntityKind }
  nodes: TraverseNode[]
  edges: TraverseEdge[]
}

export interface QueryLayer {
  list<T = unknown>(kind: EntityKind, opts?: ListOptions): Promise<T[]>
  /**
   * F 282 — `list`, with a stable page boundary and the fleet-scope check.
   * Additive: `list` is unchanged and keeps its `T[]` shape.
   */
  listPage<T = unknown>(
    kind: EntityKind,
    opts?: ListOptions,
    caller?: QueryCaller,
  ): Promise<Page<T>>
  resolve<T = unknown>(id: string): Promise<ResolvedEntity<T> | null>
  traverse(id: string, opts?: TraverseOptions): Promise<TraversalResult>
}

// ─── Link fields — declarative metadata (NOT a kind modification) ─────────

/**
 * Per-kind list of fields that hold entity references (by id or by fcid).
 * This is metadata, not a kind change. It tells the query layer which
 * fields to walk when resolving / traversing.
 *
 * Rules per field:
 *   - field value is a string: treat as id OR fcid; load entity by id
 *   - field value is string[]: treat each element as id or fcid; load each
 *   - field is a dotted path with '[]' segments (e.g. 'messages[].author'):
 *     NOT supported in v1; the message meta blob carries ids via fcid
 *     resolution at the consumer level
 */
export const LINK_FIELDS: Partial<Record<EntityKind, string[]>> = {
  // ── FVC 170 kinds (data-model-only) ──
  prompt: ['parentId'],                // id form
  build: ['parentId'],                 // id form (REQUIRED → Prompt)
  thread: ['cards'],                   // array of mixed-kind ids
  conversation: [],                    // participants are operator/agent ids, not entity refs
  decision: ['parentId', 'refs'],      // refs is array of fcid:// refs
  group: ['entities', 'parentId'],     // entities is mixed-kind ids; parent is group-id
  // ── FVC 172-spine kinds (chat migration spine) ──
  turn: ['conversationId', 'agentId', 'parentTurnId', 'spawnedAgentId'],
  agent: ['spawnedByTurn'],
  span: ['turnId', 'parentSpanId', 'agentId'],
  // ── Existing file-classifiable kinds ──
  page: ['parentId', 'children'],      // page.parentId + children[]
  task: ['parentId', 'pageId'],        // task.parentId (sub-task) + pageId (backlink)
  // Other existing kinds (record, asset, scene, derived, source-asset,
  // project, artifact) have no entity-to-entity links at v1.
}

const KEY_PREFIX = (kind: EntityKind): string => `${kind}:`

/** Parse an fcid://<kind>/<id> URI to extract the bare id. */
function fcidToId(value: string): string {
  if (typeof value !== 'string') return value
  if (value.startsWith('fcid://')) {
    const slash = value.indexOf('/', 'fcid://'.length)
    if (slash > 0) return value.slice(slash + 1)
  }
  return value
}

/** Extract the kind from an fcid://<kind>/<id> URI, or null. */
function fcidToKind(value: string): EntityKind | null {
  if (typeof value !== 'string' || !value.startsWith('fcid://')) return null
  const rest = value.slice('fcid://'.length)
  const slash = rest.indexOf('/')
  if (slash < 0) return null
  return rest.slice(0, slash) as EntityKind
}

const DEFAULT_TRAVERSE_DEPTH = 3
const MAX_TRAVERSE_DEPTH = 8

// ─── QueryLayer implementation ─────────────────────────────────────────────

export function createQuery(adapter: StorageAdapter): QueryLayer {
  if (!adapter || typeof adapter.list !== 'function' || typeof adapter.load !== 'function') {
    throw new Error('[QueryLayer] adapter must implement StorageAdapter (list, load required)')
  }

  // ── list ────────────────────────────────────────────────────────────────

  async function list<T = unknown>(kind: EntityKind, opts: ListOptions = {}): Promise<T[]> {
    if (!kind || typeof kind !== 'string') {
      throw new Error('[QueryLayer.list] kind is required')
    }
    // Fetch all keys with the kind prefix. Adapter.list is storage-agnostic.
    let keys: string[]
    try {
      keys = await adapter.list(KEY_PREFIX(kind))
    } catch (err) {
      // Adapter failure surfaces as a QueryLayer error.
      throw new Error(`[QueryLayer.list] adapter.list failed for kind "${kind}": ${(err as Error).message}`)
    }
    if (!keys || keys.length === 0) return []

    // Load all matching keys. Done in parallel for storage adapters that
    // benefit (PocketBase); cheap on LocalStorage/IndexedDB.
    const loaded: T[] = []
    const loadPromises = keys.map(async (k) => {
      try {
        const v = await adapter.load(k, { adapter: 'query' })
        if (v !== undefined && v !== null) return v as T
      } catch {
        // Per-record load errors are skipped (don't fail the whole list)
      }
      return null
    })
    const results = await Promise.all(loadPromises)
    for (const r of results) if (r !== null) loaded.push(r)

    // Apply in-memory filters
    let filtered = loaded
    if (opts.project !== undefined) {
      filtered = filtered.filter((e: any) => e && e.project === opts.project)
    }
    if (opts.after !== undefined) {
      filtered = filtered.filter((e: any) => e && typeof e.createdAt === 'string' && e.createdAt > opts.after!)
    }
    if (opts.before !== undefined) {
      filtered = filtered.filter((e: any) => e && typeof e.createdAt === 'string' && e.createdAt < opts.before!)
    }
    if (typeof opts.offset === 'number' && opts.offset > 0) {
      filtered = filtered.slice(opts.offset)
    }
    if (typeof opts.limit === 'number' && opts.limit > 0) {
      filtered = filtered.slice(0, opts.limit)
    }
    return filtered
  }

  // ── listPage (F 282) ───────────────────────────────────────────────────

  /**
   * `list` with a stable page boundary and the fleet-scope check.
   *
   * The read path is `list` itself — the query logic is NOT duplicated, so a
   * filter fixed in one is fixed in both. The only thing added is an ORDER BY,
   * which `list` does not do: `list` returns adapter order, and a cursor over
   * an unordered sequence is not a cursor, it is a coincidence.
   */
  async function listPage<T = unknown>(
    kind: EntityKind,
    opts: ListOptions = {},
    caller?: QueryCaller,
  ): Promise<Page<T>> {
    if (!kind || typeof kind !== 'string') {
      throw new QueryLayerError('[QueryLayer.listPage] kind is required')
    }
    if (typeof opts.limit !== 'number' || opts.limit <= 0) {
      throw new QueryLayerError('[QueryLayer.listPage] limit must be a positive number')
    }

    // FLEET-SCOPE. Reading your own records needs no capability. Reading ACROSS
    // projects — no `project` filter — needs a declared one. The check lives
    // here, not in the route, so every caller inherits it and a route cannot
    // widen access by omission.
    if (opts.project === undefined && !caller?.hasFleetRead) {
      throw new QueryLayerError(
        '[QueryLayer.listPage] cross-project read refused: ' +
          `"${caller?.project ?? '<unknown>'} does not hold the 'fleet-read' capability`,
      )
    }

    // Same read path as `list`. `offset` is intentionally NOT passed through:
    // combining a positional skip with a cursor makes the token meaningless.
    const all = await list<T>(kind, {
      project: opts.project,
      after: opts.after,
      before: opts.before,
    })

    const ordered = [...all].sort((a, b) => {
      const ac = String((a as any)?.createdAt ?? '')
      const bc = String((b as any)?.createdAt ?? '')
      if (ac !== bc) return ac < bc ? -1 : 1
      const ai = String((a as any)?.id ?? '')
      const bi = String((b as any)?.id ?? '')
      return ai < bi ? -1 : ai > bi ? 1 : 0
    })

    let start = 0
    if (opts.cursor !== undefined) {
      const at = decodeCursor(opts.cursor)
      start = ordered.findIndex(
        (e) =>
          String((e as any)?.createdAt ?? '') > at.c ||
          (String((e as any)?.createdAt ?? '') === at.c && String((e as any)?.id ?? '') > at.i),
      )
      // A cursor past the end means the page was already consumed. That is an
      // empty last page, not an error.
      if (start === -1) start = ordered.length
    }

    const rows = ordered.slice(start, start + opts.limit)
    const consumed = start + rows.length
    const hasMore = consumed < ordered.length
    const last = rows[rows.length - 1]
    const nextCursor =
      hasMore && last
        ? encodeCursor({
            c: String((last as any)?.createdAt ?? ''),
            i: String((last as any)?.id ?? ''),
          })
        : null

    return { rows, nextCursor }
  }

  // ── resolve ─────────────────────────────────────────────────────────────

  /** Internal: load an entity by id. Tries kind-prefixed key first; falls back
   *  to scanning all keys if the kind isn't determinable from the id. */
  async function loadById(id: string): Promise<{ entity: any; fcid: string; kind: EntityKind } | null> {
    if (!id || typeof id !== 'string') return null
    // Strategy: try common kind prefixes (cheap for LocalStorage; PB does
    // a filter-by-key which is also fast). 15 kinds × 1 lookup is fine.
    const candidateKinds: EntityKind[] = [
      'page', 'record', 'asset', 'scene', 'derived', 'source-asset',
      'task', 'project', 'artifact',
      'prompt', 'build', 'thread', 'conversation', 'decision', 'group',
    ]
    for (const k of candidateKinds) {
      try {
        const v = await adapter.load(`${KEY_PREFIX(k)}${id}`, { adapter: 'query' })
        if (v !== undefined && v !== null && typeof v === 'object' && (v as any).id === id) {
          return { entity: v, fcid: (v as any).fcid ?? `fcid://${k}/${id}`, kind: k }
        }
      } catch {
        // continue
      }
    }
    return null
  }

  async function resolve<T = unknown>(id: string): Promise<ResolvedEntity<T> | null> {
    if (!id || typeof id !== 'string') return null
    const found = await loadById(id)
    if (!found) return null

    const entity = found.entity as T
    const fcid = found.fcid
    const kind = found.kind
    const linkFields = LINK_FIELDS[kind] ?? []
    const links: ResolvedEntity<T>['links'] = []

    for (const field of linkFields) {
      const value = (entity as any)?.[field]
      if (value === null || value === undefined) continue
      if (typeof value === 'string') {
        // Single id (or fcid)
        const targetId = fcidToId(value)
        const targetKind = fcidToKind(value)
        // Helper: load by id (or kind-prefixed id) and return the entity object
        // (NOT the loadById wrapper). This is the single source of truth for
        // "load an entity by id" inside resolve.
        const loadedEntity: any = targetKind
          ? await adapter.load(`${KEY_PREFIX(targetKind)}${targetId}`, { adapter: 'query' })
          : (await loadById(targetId))?.entity ?? null
        if (loadedEntity !== null && typeof loadedEntity === 'object' && loadedEntity.id) {
          links.push({
            field,
            target: {
              id: loadedEntity.id,
              fcid: loadedEntity.fcid ?? `fcid://${targetKind ?? 'unknown'}/${loadedEntity.id}`,
              entity: loadedEntity as T,
            },
          })
        } else {
          links.push({ field, target: { id: targetId, fcid: `fcid://unknown/${targetId}`, entity: null } })
        }
      } else if (Array.isArray(value)) {
        // Array of ids / fcids
        for (const v of value) {
          if (typeof v !== 'string') continue
          const targetId = fcidToId(v)
          const targetKind = fcidToKind(v)
          const loadedEntity: any = targetKind
            ? await adapter.load(`${KEY_PREFIX(targetKind)}${targetId}`, { adapter: 'query' })
            : (await loadById(targetId))?.entity ?? null
          if (loadedEntity !== null && typeof loadedEntity === 'object' && loadedEntity.id) {
            links.push({
              field,
              target: {
                id: loadedEntity.id,
                fcid: loadedEntity.fcid ?? `fcid://${targetKind ?? 'unknown'}/${loadedEntity.id}`,
                entity: loadedEntity as T,
              },
            })
          } else {
            links.push({ field, target: { id: targetId, fcid: `fcid://unknown/${targetId}`, entity: null } })
          }
        }
      }
    }

    return { entity, fcid, links }
  }

  // ── traverse ────────────────────────────────────────────────────────────

  async function traverse(id: string, opts: TraverseOptions = {}): Promise<TraversalResult> {
    if (!id || typeof id !== 'string') {
      throw new Error('[QueryLayer.traverse] id is required')
    }
    let depth = opts.depth ?? DEFAULT_TRAVERSE_DEPTH
    if (typeof depth !== 'number' || Number.isNaN(depth) || depth < 0) {
      throw new Error('[QueryLayer.traverse] depth must be a non-negative number')
    }
    if (depth > MAX_TRAVERSE_DEPTH) {
      depth = MAX_TRAVERSE_DEPTH
    }
    const allowedKinds = opts.kinds && opts.kinds.length > 0 ? new Set(opts.kinds) : null

    const root = await loadById(id)
    if (!root) {
      return { root: { id, fcid: `fcid://unknown/${id}`, kind: 'page' }, nodes: [], edges: [] }
    }
    // Root is always in the result (the user named it explicitly). The kinds
    // filter restricts which CHILDREN get walked, not whether the root appears.

    const nodes: TraverseNode[] = [{ id: root.entity.id, fcid: root.fcid, kind: root.kind, depth: 0 }]
    const edges: TraverseEdge[] = []
    const visited = new Set<string>([root.entity.id])

    // BFS by depth
    type Frontier = { id: string; fcid: string; kind: EntityKind; depth: number }
    let frontier: Frontier[] = [{ id: root.entity.id, fcid: root.fcid, kind: root.kind, depth: 0 }]

    while (frontier.length > 0) {
      const nextFrontier: Frontier[] = []
      for (const node of frontier) {
        if (node.depth >= depth) continue
        const linkFields = LINK_FIELDS[node.kind] ?? []
        // Need the entity object to walk its link fields. Re-resolve.
        const resolved = await resolve(node.id)
        if (!resolved) continue
        for (const link of resolved.links) {
          if (!link.target || !link.target.id) continue
          // We don't know the target's kind from resolve's link metadata; sniff it from the fcid
          const targetKind = fcidToKind(link.target.fcid) as EntityKind | null
          if (allowedKinds && targetKind && !allowedKinds.has(targetKind)) continue
          if (visited.has(link.target.id)) {
            // Edge to a visited node (cycle). Record edge, don't recurse.
            edges.push({ from: node.id, to: link.target.id, field: link.field })
            continue
          }
          visited.add(link.target.id)
          const targetFcid = link.target.fcid
          if (targetKind) {
            nodes.push({ id: link.target.id, fcid: targetFcid, kind: targetKind, depth: node.depth + 1 })
            edges.push({ from: node.id, to: link.target.id, field: link.field })
            nextFrontier.push({ id: link.target.id, fcid: targetFcid, kind: targetKind, depth: node.depth + 1 })
          }
        }
      }
      frontier = nextFrontier
    }

    return { root: { id: root.entity.id, fcid: root.fcid, kind: root.kind }, nodes, edges }
  }

  return { list, listPage, resolve, traverse }
}

// ─── Errors ────────────────────────────────────────────────────────────────

export class QueryLayerError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'QueryLayerError'
  }
}
